import React, {Fragment} from "react";
import {connect} from "react-redux";
import {
    Input,
    Space,
    Popover,
    Descriptions,
    InputNumber,
    Table,
    Button,
    Select,
    Modal,
    Radio,
    message, Tooltip, Row, Col, Tag, Form, Alert, Card, Divider
} from "antd";
import {
    createIPAddressOrder, createQuickIPAddressOrder,
    loadAvailableIPAddresses,
    loadIPAddressBillingCycles,
    loadIPAddressGroups
} from "../../actions/ip_addresses";
import {displayErrors, isNumeric, normalizeEnum} from "../../libs/utils";
import {MoneyField} from "../shared/MoneyField";
import QuestionCircleOutlined from "@ant-design/icons/lib/icons/QuestionCircleOutlined";
import {Loading} from "../../libs/loading";
import AccessItem from "../permissions/AccessItem";
import {FullscreenExitOutlined, FullscreenOutlined} from "@ant-design/icons";
import {driverNextStep, updateDriverMultipleHighlights} from "../../actions/driver";
import {isMobile} from 'react-device-detect';
import "/node_modules/flag-icons/css/flag-icons.min.css";

class NewIPAddressesWizard extends React.Component {
    state = {
        page: 0,
        ipType: 'DEDICATED',
        ipFilter: 'C',
        maxSites: 10,
        loadingAvailableIPAddresses: false,
        data: [],
        selectedIPAddresses: [],
        ipGroups: [],
        excludedIPGroups: [],
        ipCount: 0,
        cityCount: 0,
        countryCount: 0,
        billingCycle: 'ANNUALLY',
        coupon: '',
        creatingOrder: false,
        loadingIPGroups: false,
        ipGroupsLoaded: false,
        loadingBillingCycles: false,
        availableBillingCycles: [],
        expandedIPAddressRows: [],
        orderOverviewInView: false,

        quickOrderIPCount: 5,
        creatingQuickOrder: false,
    }

    orderOverviewRef = React.createRef();

    componentWillUnmount() {
        this.observer?.disconnect();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if(this.props.show) {
            if(this.orderOverviewRef.current !== null && typeof(this.observer) === 'undefined' && !isMobile) {
                this.observer = new IntersectionObserver(
                    ([entry]) => {
                        this.setState({ orderOverviewInView: entry.isIntersecting });
                    },
                    {
                        threshold: 1,
                        rootMargin: "-70px"
                    }
                );

                this.observer.observe(this.orderOverviewRef.current);
            }
        }

        if(this.props.show && !this.state.loadingIPGroups && !this.state.ipGroupsLoaded) {
            this.setState({ excludedIPGroups: [this.props.group] });

            this.loadIPAddressGroups();
            this.loadBillingCycles();

            if(this.props.wizard.currentStep === 4 && this.state.page === 1) {
                setTimeout(() => {
                    this.props.updateDriverMultipleHighlights([
                        {
                            element: 'tr:has(> th.ip-address-wizard-ip-address-type)',
                            popover: {
                                title: 'IP Address Type',
                                description: 'Here you can choose between dedicated and shared IP addresses. Dedicated IP addresses are only available for you. Shared IP addresses are shared among other customers.'
                            }
                        },
                        {
                            element: 'tr:has(> th.ip-address-wizard-ip-address-subnet-filter)',
                            popover: {
                                title: 'IP Address Subnet Filter',
                                description: 'The IP address subnet filter allows you to filter IP addresses based on their subnet. '
                            }
                        },
                        {
                            element: 'tr:has(> th.ip-address-wizard-exclude-ip-groups)',
                            popover: {
                                title: 'Exclude IP Groups',
                                description: 'The exclude IP groups option allows you to exclude specific IP address groups from the IP address selection. This ensures you get unique IP addresses across all of your IP address groups.'
                            }
                        }
                    ]);
                }, 500);
            }
        }
    }

    loadIPAddressGroups() {
        this.setState({ loadingIPGroups: true });

        this.props.loadIPAddressGroups(1, 99999, null, (res) => {
            let ipGroups = [];

            for(let i = 0; i < res.data.data.length; i++) {
                ipGroups.push({ guid: res.data.data[i].guid, name: res.data.data[i].name});
            }

            this.setState({ ipGroups: ipGroups, loadingIPGroups: false, ipGroupsLoaded: true });
        }, (err) => {
            this.setState({ loadingIPGroups: false, ipGroupsLoaded: true });
            displayErrors(err.response.data);
        });
    }

    loadBillingCycles() {
        this.setState({ loadingBillingCycles: true });

        this.props.loadIPAddressBillingCycles((res) => {
            this.setState({ loadingBillingCycles: false, availableBillingCycles: res.data });
        }, (err) => {
            this.setState({ loadingBillingCycles: false });
            displayErrors(err.response.data);
        });
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if(nextProps !== prevState) {
            return nextProps;
        }

        return null;
    }

    closeModal() {
        this.setState({
            page: 0,
            ipType: 'DEDICATED',
            ipFilter: 'C',
            maxSites: 10,
            loadingAvailableIPAddresses: false,
            data: [],
            selectedIPAddresses: [],
            ipGroups: [],
            excludedIPGroups: [],
            ipCount: 0,
            cityCount: 0,
            countryCount: 0,
            billingCycle: 'ANNUALLY',
            coupon: '',
            creatingOrder: false,
            loadingIPGroups: false,
            ipGroupsLoaded: false,
            loadingBillingCycles: false,
            availableBillingCycles: [],
            expandedIPAddressRows: [],
            orderOverviewInView: false,
            quickOrderIPCount: 5,
            quickOrderLocation: 'UNITED_STATES'
        });

        this.props.close();
    }

    loadAvailableIPAddresses() {
        this.setState({ loadingAvailableIPAddresses: true });

        this.props.loadAvailableIPAddresses(this.state.ipType, this.state.ipFilter, this.state.excludedIPGroups, this.props.user.user.company.currency, (res) => {
            let countryCount = 0;
            let cityCount = 0;
            let ipCount = 0;

            for(let i = 0; i < res.data.length; i++) {
                countryCount++;

                let cities = [];

                for(let j = 0; j < res.data[i].locations.length; j++) {
                    if(cities.indexOf(res.data[i].locations[j].city) === -1) {
                        cities.push(res.data[i].locations[j].city);
                        cityCount++;
                    }

                    ipCount += res.data[i].locations[j].ip_address_count;
                }
            }

            if(ipCount === 0) {
                message.warning('No available IP addresses found! Please change your targeting preferences!', 5);
                this.setState({ page: 1 });
            }
            else
            {
                this.setState({ loadingAvailableIPAddresses: false, data: res.data, ipCount: ipCount, cityCount: cityCount, countryCount: countryCount });

                if(this.props.wizard.currentStep === 4 && this.state.page === 2) {
                    setTimeout(() => {
                        this.props.updateDriverMultipleHighlights([
                            {
                                element: '.ip-address-wizard-found',
                                popover: {
                                    description: 'Here you can see the locations our system found to match your criteria set in the previous step.'
                                }
                            },
                            {
                                element: '.ip-address-wizard-table',
                                popover: {
                                    description: 'Here you can see all the available locations that match your criteria. Click on the country row or the [+] button in the beginning of the country row to expand the location details.',
                                    onNextClick: () => {
                                        document.querySelector('.ip-address-wizard-table .ant-table-row.ant-table-row-level-0').click();
                                        this.props.driverNextStep();
                                    }
                                }
                            },
                            {
                                element: '.ant-table-cell tr[data-row-key].ant-table-row.ant-table-row-level-0',
                                popover: {
                                    description: '<div style="font-size: 14px">' +
                                        '<p>Here you can see available cities of the selected country. You can also see how many IP addresses are available in each city and how much they cost.</p>' +
                                        '<p><b>The "IP count" field</b> is for setting how many IP addresses you wish to order from that specific city.</p>' +
                                        '<p><b>The "Site count" field</b> sets how many domain slots per IP address will be added to your order. By default, every IP address comes with 1 domain slot.</p>' +
                                        '<p>Click on "Done" to close this help message and select your IP addresses from any location you wish.</p>' +
                                        '<p>If you are ready to place the order click on the "Next" button below.</p>' +
                                        '</div>'
                                }
                            }
                        ]);
                    }, 500);
                }
            }

        }, (err) => {
            this.setState({ loadingAvailableIPAddresses: false, page: 1 });
            displayErrors(err.response.data);
        });
    }

    calculateIPAddressCount(data) {
        let amount = 0;

        for(let i = 0; i < data.locations.length; i++) {
            amount += data.locations[i].ip_address_count;
        }

        return amount + ' IP addresses';
    }

    updateItemCountValue(record, count, field) {
        // TODO: Get account limits

        if(field === 'site_count' && !isNumeric(count)) {
            return message.error('Site count must be a number!');
        }

        if(field === 'ip_address_count' && !isNumeric(count)) {
            return message.error('IP address count must be a number!');
        }

        if(field === 'site_count' && count > 10) {
            return message.error('Website slot count cannot be larger than 10!');
        }

        if(field === 'ip_address_count' && count > record.ip_address_count) {
            return message.error('IP address count cannot be larger than ' + record.ip_address_count + '!');
        }

        if(field === 'ip_address_count' && count === 0)
        {
            let newData = this.state.selectedIPAddresses.filter((data) => data.location !== record.location && data.id !== record.id);
            this.setState({ selectedIPAddresses: newData });
        }
        else
        {
            let item = null;

            for(let i = 0; i < this.state.selectedIPAddresses.length; i++) {
                if(this.state.selectedIPAddresses[i].location === record.location && this.state.selectedIPAddresses[i].id === record.id) {
                    item = this.state.selectedIPAddresses[i];
                    break;
                }
            }

            if(item === null) {
                item = {...record, ip_address_count: 0, site_count: 1}
            }

            if(field === 'site_count' && count === 0) {
                count = 1;
            }

            item[field] = count;

            if(field === 'site_count' && item.ip_address_count === 0)
            {
                let newData = this.state.selectedIPAddresses.filter((data) => data.location !== record.location && data.id !== record.id);
                return this.setState({ selectedIPAddresses: newData });
            }

            let newData = this.state.selectedIPAddresses.map((ip) => {
                if(ip.location === record.location && ip.id === record.id) {
                    return item;
                }

                return ip;
            });

            let itemFound = false;
            for(let i = 0; i < newData.length; i++) {
                if(newData[i].location === record.location && newData[i].id === record.id) {
                    itemFound = true;
                    break;
                }
            }

            if(!itemFound) {
                newData.push(item);
            }

            this.setState({ selectedIPAddresses: newData });
        }
    }

    getItemCount(record, field) {
        let value = 0;

        for(let i = 0; i < this.state.selectedIPAddresses.length; i++) {
            if(this.state.selectedIPAddresses[i].location === record.location && this.state.selectedIPAddresses[i].id === record.id) {
                return this.state.selectedIPAddresses[i][field];
            }
        }

        return value;
    }

    placeOrder() {
        let locations = this.state.selectedIPAddresses.map((data) => {
            return {
                pid: data.id,
                location: data.location,
                ip_address_count: data.ip_address_count,
                site_count: data.site_count
            }
        });

        if(locations.length === 0) {
            return message.error('Please choose at least 1 location!');
        }

        let coupon = this.state.coupon.trim();

        let data = {
            group: this.props.group,
            billing_cycle: this.state.billingCycle,
            locations: locations,
            ip_address_filter: this.state.ipFilter,
            ip_address_type: this.state.ipType,
            excluded_groups: this.state.excludedIPGroups,
            coupon: coupon.length !== 0 ? coupon : null
        };

        this.setState({ creatingOrder: true });

        this.props.createIPAddressOrder(data, (res) => {
            message.success('Order successfully placed, redirecting to invoice...', 5);
            this.props.history.push('/billing/invoices/' + res.data.invoice_id);
            this.setState({ creatingOrder: false });
        }, (err) => {
            if(typeof err.response !== 'undefined') {
                this.setState({ creatingOrder: false });
                displayErrors(err.response.data);
            }
        });
    }

    chooseIPAddressFilter(value) {
        if(value === 'NONE') {
            const { confirm } = Modal;

            confirm({
                centered: true,
                okText: 'Yes, I am sure!',
                okType: 'danger',
                icon: false,
                title: <strong>Are you sure?</strong>,
                content: <div>
                    <p>Disabling filtering means that you will receive random IP addresses <u><b>from any subnet</b></u>.
                        You may receive IP addresses from subnets <u><b>that already exist in your IP address group(s)</b></u>.</p>
                    <p>Proceed at your caution!</p>
                </div>,
                onOk: () => {
                    this.setState({ ipFilter: value });
                }
            });
        } else {
            this.setState({ ipFilter: value });
        }
    }

    updateExpandedIPAddressRows(expanded, data) {
        let newExpandedIPAddressRows = this.state.expandedIPAddressRows;

        if(expanded) {
            newExpandedIPAddressRows.push(data.country_code);
        } else {
            newExpandedIPAddressRows = newExpandedIPAddressRows.filter(row => row !== data.country_code);
        }

        this.setState({ expandedIPAddressRows: newExpandedIPAddressRows });
    }

    expandAllIPAddressRows() {
        let newExpandedIPAddressRows = this.state.data.map(data => data.country_code);
        this.setState({ expandedIPAddressRows: newExpandedIPAddressRows });
    }

    closeAllExpandedIPAddressRows() {
        this.setState({ expandedIPAddressRows: [] });
    }

    showOrderOverview() {
        this.setState({ page: 3 }, () => {
            if(this.props.wizard.currentStep === 4 && this.state.page === 3) {
                setTimeout(() => {
                    this.props.updateDriverMultipleHighlights([
                        {
                            element: '.ip-address-wizard-order-summary-table',
                            popover: {
                                description: 'Here you can see your order summary.'
                            }
                        },
                        {
                            element: '.ip-address-wizard-order-summary-coupon-billing-cycle-fields',
                            popover: {
                                description: 'Here you can enter a coupon code, if you have one, and you can choose the billing cycle for your order.',
                            }
                        },
                        {
                            element: '.ip-address-wizard-place-order-btn',
                            popover: {
                                description: 'Click here to place your order. You will be redirected to the invoice page after successful order placement.',
                            }
                        }
                    ]);
                }, 500);
            }
        })
    }

    calculateQuickOrderCost() {
        const currency = this.props.user.user.company.currency;
        let ipPrice = 2.89;

        if(currency === 'EUR') {
            ipPrice = ipPrice * this.props.user.user.company.conversion_rate;
        }

        return this.state.quickOrderIPCount * ipPrice;
    }

    placeQuickOrder() {
        let data = {
            ip_address_count: this.state.quickOrderIPCount,
            group: this.props.group,
        };

        this.setState({ creatingQuickOrder: true });

        this.props.createQuickIPAddressOrder(data, (res) => {
            message.success('Quick order successfully placed, redirecting to invoice...', 5);

            this.props.history.push('/billing/invoices/' + res.data.invoice_id);

            this.setState({ creatingQuickOrder: false });
        }, (err) => {
            if(typeof err.response !== 'undefined') {
                this.setState({ creatingQuickOrder: false });
                displayErrors(err.response.data);
            }

        });
    }

    render() {
        if(this.state.loadingIPGroups) {
            return <div className='text-center'><Loading /></div>;
        }

        let content = '';
        let footerButtons = '';

        let { Option } = Select;

        if(this.state.page === 0) {
            content = <div>
                <Row gutter={[16, 16]}>
                    <Col md={12} xs={24}>
                        <Card bordered={false} title={<div className='text-center'>Quick Order</div>} actions={[
                            <div>
                                <Button loading={this.state.creatingQuickOrder} disabled={this.state.creatingQuickOrder} type='primary' onClick={() => this.placeQuickOrder()}>Order Now</Button>
                                <div><small>* excluding VAT</small></div>
                            </div>
                        ]}>
                            <div style={{padding: '10px'}}>
                                <Select style={{width: '100%'}} value={this.state.quickOrderIPCount} onChange={(value) => this.setState({ quickOrderIPCount: value })}>
                                    <Option value={5}>5 IP addresses</Option>
                                    <Option value={10}>10 IP addresses</Option>
                                    <Option value={15}>15 IP addresses</Option>
                                    <Option value={20}>20 IP addresses</Option>
                                    <Option value={30}>30 IP addresses</Option>
                                    <Option value={50}>50 IP addresses</Option>
                                </Select>
                            </div>
                            <Divider style={{margin: '10px 0'}} />
                            <div className='text-center'>
                                IP address location: <b>United States</b>
                            </div>
                            <Divider style={{margin: '10px 0'}} />
                            <div className='text-center' style={{marginTop: '10px'}}>Dedicated IP addresses</div>
                            <Divider style={{margin: '10px 0'}} />
                            <div className='text-center'>Unique C classes</div>
                            <Divider style={{margin: '10px 0'}} />
                            <div className='text-center'>
                                <div style={{fontSize: '21px', fontWeight: '500'}}>
                                    <MoneyField amount={this.calculateQuickOrderCost()} currency={this.props.user.user.company.currency} />
                                    <span style={{fontSize: '12px', fontWeight: '400'}}>/per month*</span>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col md={12} xs={24}>
                        <Card bordered={false} title={<div className='text-center'>Custom Order</div>} actions={[
                            <div>
                                <Button onClick={() => this.setState({ page: 1})}>Select IP Addresses</Button>
                                <div><small>* excluding VAT</small></div>
                            </div>
                        ]}>
                            <div className='text-center' style={{marginTop: '7px'}}>1,000+ IP addresses available</div>
                            <Divider style={{margin: '10px 0'}} />
                            <div className='text-center'><b>46 locations available</b></div>
                            <Divider style={{margin: '10px 0'}} />
                            <div className='text-center'>Dedicated & shared IP addresses</div>
                            <Divider style={{margin: '10px 0'}} />
                            <div className='text-center'>Filter by A/B/C class</div>
                            <Divider style={{margin: '10px 0'}} />
                            <div className='text-center'>
                                IP address cost starts from:
                                <div style={{fontSize: '21px', fontWeight: '500'}}>
                                    <MoneyField amount={this.props.user.user.company.currency === 'EUR' ? 2.3*this.props.user.user.company.conversion_rate : 2.3} currency={this.props.user.user.company.currency} />
                                    <span style={{fontSize: '12px', fontWeight: '400'}}>/per month*</span>
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>
                <Divider style={{margin: '0 0 10px 0'}} />
                <div style={{color: 'rgba(0, 0, 0, 0.6)', fontSize: '12px'}}>
                    <div>Your subscription will renew automatically at the end of each billing term unless canceled before the renewal date via the customer panel.</div>
                    <div>By completing this order, you confirm that the Services are provided in accordance with Priority Prospect OÜ's <a href='https://priorityprospect.com/legal/terms-of-service/' target='_blank' rel='noreferrer noopener'>Terms of Service</a> including billing, renewal, and refund provisions.</div>
                </div>
            </div>
        } else if(this.state.page === 1) {
            content = <Fragment key='step-1'>
                <p>Please select filters for IP addresses that you wish to order. Click on question mark icon for more information.</p>
                <Descriptions bordered size='small' column={1}>
                    <Descriptions.Item label='IP address type:' className='ip-address-wizard-ip-address-type'>
                        <Radio.Group defaultValue={this.state.ipType} onChange={(e) => this.setState({ ipType: e.target.value, maxSites: (e.target.value === 'DEDICATED' ? 10 : 1) })}>
                            <Radio value='DEDICATED' style={{display: 'block'}}>
                                <Space>
                                    <span>Dedicated IP addresses</span>
                                    <Popover content={<div style={{maxWidth: '250px'}}>
                                        <p>Best for tier 1 networks and high-value domains that require maximum uniqueness.</p>
                                        <p><b>Dedicated IP addresses are only available for you!</b></p>
                                    </div>} title="Dedicated IP address">
                                        <QuestionCircleOutlined />
                                    </Popover>
                                </Space>
                            </Radio>
                            <Radio value='SHARED' style={{display: 'block'}}>
                                <Space>
                                    <span>Shared IP addresses</span>
                                    <Popover content={<div style={{maxWidth: '250px'}}>
                                        <p>A smart choice for tier 2/3 networks and smaller projects that only require a unique IP address.</p>
                                        <p><b>Shared IP addresses are shared with other domains!</b></p>
                                    </div>} title="Shared IP address">
                                        <QuestionCircleOutlined />
                                    </Popover>
                                </Space>
                            </Radio>
                        </Radio.Group>
                    </Descriptions.Item>
                    <Descriptions.Item label='IP address subnet filter:' className='ip-address-wizard-ip-address-subnet-filter'>
                        <Radio.Group value={this.state.ipFilter} onChange={(e) => this.chooseIPAddressFilter(e.target.value)}>
                            <Radio value='NONE' style={{display: 'block'}}>No filter</Radio>
                            <Radio value='C' style={{display: 'block'}}>"C" classes</Radio>
                            <Radio value='B' style={{display: 'block'}}>"B" classes</Radio>
                            <Radio value='A' style={{display: 'block'}}>"A" classes</Radio>
                        </Radio.Group>
                    </Descriptions.Item>
                    <Descriptions.Item style={{width: '50%'}} label='Exclude subnets in IP groups:' className='ip-address-wizard-exclude-ip-groups'>
                        <Select defaultValue={this.state.excludedIPGroups} mode='multiple'
                                dropdownStyle={{width: '100%'}} style={{width: '100%'}}
                                onChange={(value) => this.setState({ excludedIPGroups: value })}>
                            {this.state.ipGroups.map((item) => {
                                return <Option key={item.guid} value={item.guid}>{item.name}</Option>
                            })}
                        </Select>
                    </Descriptions.Item>
                </Descriptions>
            </Fragment>;

            footerButtons = <Row>
                <Col span={12} className='text-left'>
                    <Button type='default' onClick={() => this.closeModal()}>Cancel</Button>
                </Col>
                <Col span={12}>
                    <Button onClick={() => this.setState({ page: 0 })}>Previous</Button>
                    <Button type='primary' onClick={() => this.setState({ page: 2 }, () => this.loadAvailableIPAddresses())}>Next</Button>
                </Col>
            </Row>;
        }
        else if(this.state.page === 2)
        {
            const columns = [
                { title: 'Location', render: (record) => <span>
                        <span className={'fi fi-' + record.country_code.toLowerCase()}></span> {record.country_name}
                </span> },
                { title: 'Available', render: (record) => this.calculateIPAddressCount(record) },
            ];

            const subTableColumns = [
                { title: <span>City <Tooltip title='The location of the IP address.'><small><QuestionCircleOutlined /></small></Tooltip></span>, dataIndex: 'city' },
                { title: <span>Available <Tooltip title='How many IP addresses are available from a datacenter with the specific price.'><small><QuestionCircleOutlined /></small></Tooltip></span>, dataIndex: 'ip_address_count', align: 'center', width: '15%' },
                { title: <span>Cost per month <Tooltip title='The cost per IP address per month.'><small><QuestionCircleOutlined /></small></Tooltip></span>, dataIndex: 'price', align: 'center', width: '20%', render: (record, item) => {
                        if(item.currency === 'EUR') {
                            return <Tooltip title={<span>The base rate is in USD. Currency conversion rates are updated daily. Displayed prices are approximate. The final and binding amount is shown on the invoice.</span>}>≈ <MoneyField amount={item.price} currency={item.currency} /></Tooltip>;
                        } else {
                            return <MoneyField amount={item.price} currency={item.currency} />;
                        }
                }},
                { title: <span>IP count <Tooltip title='The number of IP addresses you wish to order.'><small><QuestionCircleOutlined /></small></Tooltip></span>, align: 'center', width: '15%', render: (record) => <InputNumber min={0} max={record.ip_address_count} value={this.getItemCount(record, 'ip_address_count')} onChange={(value) => this.updateItemCountValue(record, value, 'ip_address_count')} />},
            ];

            if(this.state.ipType === 'DEDICATED') {
                subTableColumns.push({ title: <span>Website slots per IP <Tooltip title='Website slots are how many domains a single IP address can hold.'><small><QuestionCircleOutlined /></small></Tooltip></span>, align: 'center', width: '25%', render: (record) => <InputNumber disabled={this.state.ipType === 'SHARED'} min={0} max={this.state.maxSites} value={this.getItemCount(record, 'site_count')} onChange={(value) => this.updateItemCountValue(record, value, 'site_count')} />});
            }

            const summaryColumns = [
                { title: 'Location', render: (item, record) => <span><span className={'fi fi-' + record.country_code.toLowerCase()}></span> {record.country + ', ' + record.city}</span> },
                { title: 'IP count', align: 'center', dataIndex: 'ip_address_count' },
                { title: 'Website slots', align: 'center', dataIndex: 'site_count', render: (item) => (item - 1) },
                { title: 'Total domains', align: 'center', render: (item, record) => (record.ip_address_count * record.site_count) },
                { title: 'Cost per month', align: 'center', render: (item, record) => <MoneyField amount={(record.ip_address_count * record.price) + (((record.site_count-1) * record.site_price) * record.ip_address_count)} currency={record.currency} />}
            ];

            let orderOverviewStyles = {
                marginTop: '25px'
            };

            if(!this.state.orderOverviewInView && this.state.selectedIPAddresses.length > 0 && window.innerHeight >= 1080) {
                orderOverviewStyles = {
                    position: "fixed",
                    top: '50%',
                    left: 10,
                    transform: 'translateY(-50%)',
                    zIndex: 9999,
                    padding: '5px',
                    backgroundColor: '#fff',
                    borderRadius: '5px',
                    boxShadow: '0px 2px 4px 0px rgba(0,0,0,0.2)',
                    maxWidth: '530px'
                };
            }

            content = <Fragment key='step-2'>
                <p className='ip-address-wizard-found'>We found <b>{this.state.ipCount}</b> IP
                    address{this.state.ipCount > 1 ? 'es' : 's'} from <b>{this.state.countryCount}</b> countr{this.state.countryCount > 1 ? 'ies' : 'y'} and <b>{this.state.cityCount}</b> cit{this.state.cityCount > 1 ? 'ies' : 'y'}.
                    Below you can choose IP addresses and locations. Some cities may have IP addresses with different
                    prices.</p>
                {this.state.ipFilter === 'A' || this.state.ipFilter === 'B' ? <Alert style={{marginBottom: '10px'}} showIcon type='warning' message='Please note that unique subnets are grouped by location. The same A/B classes may exist in multiple locations.'/> : null}
                <div style={{marginBottom: '10px'}}>
                    <Space>
                        <Button size='small' icon={<FullscreenOutlined/>} onClick={() => this.expandAllIPAddressRows()}>Expand
                            All</Button>
                        <Button size='small' icon={<FullscreenExitOutlined/>}
                                onClick={() => this.closeAllExpandedIPAddressRows()}>Close All</Button>
                    </Space>
                </div>
                <div style={{overflowX: 'auto'}}>
                    <Table
                        columns={columns}
                        dataSource={this.state.data}
                        loading={this.state.loadingAvailableIPAddresses}
                        expandable={{
                            expandRowByClick: true,
                            expandedRowKeys: this.state.expandedIPAddressRows,
                            expandedRowRender: record => <Table size='small'
                                                                pagination={false}
                                                                columns={subTableColumns}
                                                                dataSource={record.locations}
                                                                rowKey={(item) => item.hash}/>,
                            rowExpandable: record => true,
                            onExpand: (expanded, record) => this.updateExpandedIPAddressRows(expanded, record)
                        }}
                        size='small'
                        className='ip-address-wizard-table'
                        rowKey={(item) => item.country_code}
                        pagination={false}
                    />
                </div>

                <div className='order-overview-observer' ref={this.orderOverviewRef} />
                    {this.state.selectedIPAddresses.length > 0 ? <div className='order-overview-info' style={orderOverviewStyles}>
                        <div style={{overflowX: 'auto'}}>
                            <Table
                                bordered
                                columns={summaryColumns}
                                dataSource={this.state.selectedIPAddresses}
                                rowKey={(item) => item.location + '-' + item.id}
                                pagination={false}
                                size='small'
                                summary={data => {
                                    let totalSum = 0;
                                    let currency = null;
                                    let totalIPs = 0;
                                    let totalSites = 0;
                                    let totalWebsites = 0;

                                    for (let i = 0; i < data.length; i++) {
                                        if (currency === null) {
                                            currency = data[i].currency;
                                        }

                                        totalIPs += data[i].ip_address_count;
                                        totalSites += (data[i].site_count - 1);
                                        totalWebsites += data[i].ip_address_count * data[i].site_count;
                                        totalSum += (data[i].ip_address_count * data[i].price) + (((data[i].site_count - 1) * data[i].site_price) * data[i].ip_address_count);
                                    }

                                    return <Fragment>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0}/>
                                            <Table.Summary.Cell index={1} className='text-center'>{totalIPs}</Table.Summary.Cell>
                                            <Table.Summary.Cell index={2} className='text-center'>{totalSites}</Table.Summary.Cell>
                                            <Table.Summary.Cell index={3} className='text-center'>{totalWebsites}</Table.Summary.Cell>
                                            <Table.Summary.Cell index={4} className='text-center'><MoneyField amount={totalSum} currency={currency}/></Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </Fragment>
                                }}
                            />
                        </div>
                    </div> : ''}

            </Fragment>;

        footerButtons = <Row>
            <Col span={12} className='text-left'>
                <Button type='default' disabled={this.state.loadingAvailableIPAddresses} onClick={() => this.closeModal()}>Cancel</Button>
            </Col>
            <Col span={12}>
                <Button type='default' disabled={this.state.loadingAvailableIPAddresses} onClick={() => this.setState({ page: 1, selectedIPAddresses: [], ipType: 'DEDICATED', expandedIPAddressRows: [] })}>Previous</Button>
                <Button type='primary' disabled={this.state.selectedIPAddresses.length === 0 || this.state.loadingAvailableIPAddresses} onClick={() => this.showOrderOverview()}>Continue</Button>
            </Col>
        </Row>;
        } else if(this.state.page === 3) {
            const columns = [
                {
                    title: 'Location',
                    render: (item, record) => <span><span className={'fi fi-' + record.country_code.toLowerCase()}></span> {record.country + ', ' + record.city}</span>
                },
                {title: 'IP count', align: 'center', dataIndex: 'ip_address_count'},
                {title: 'Website slots', align: 'center', dataIndex: 'site_count', render: (item) => (item - 1)},
                {
                    title: 'Total domains',
                    align: 'center',
                    render: (item, record) => (record.ip_address_count * record.site_count)
                },
                {
                    title: 'Cost per month',
                    align: 'center',
                    render: (item, record) => <MoneyField
                        amount={(record.ip_address_count * record.price) + (((record.site_count - 1) * record.site_price) * record.ip_address_count)}
                        currency={record.currency}/>
                }
            ];

            content = <Fragment key='step-3'>
                <p>Below you can see the overview of your order summary.</p>
                <div style={{overflowX: 'auto'}}>
                    <Table
                        bordered
                        className='ip-address-wizard-order-summary-table'
                        columns={columns}
                        dataSource={this.state.selectedIPAddresses}
                        rowKey={(item) => item.location + '-' + item.id}
                        pagination={false}
                        size='small'
                        summary={data => {
                            let totalSum = 0;
                            let currency = null;
                            let totalIPs = 0;
                            let totalSites = 0;
                            let totalWebsites = 0;

                            for (let i = 0; i < data.length; i++) {
                                if (currency === null) {
                                    currency = data[i].currency;
                                }

                                totalIPs += data[i].ip_address_count;
                                totalSites += (data[i].site_count - 1);
                                totalWebsites += data[i].ip_address_count * data[i].site_count;
                                totalSum += (data[i].ip_address_count * data[i].price) + (((data[i].site_count - 1) * data[i].site_price) * data[i].ip_address_count);
                            }

                            return <Fragment>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0}/>
                                    <Table.Summary.Cell index={1} className='text-center'>{totalIPs}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={2} className='text-center'>{totalSites}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={3} className='text-center'>{totalWebsites}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={4} className='text-center'>
                                        <MoneyField amount={totalSum} currency={currency}/>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Fragment>
                        }}
                    />
                </div>
                    <br/>
                    <Form layout='vertical'>
                        <Row gutter={[16, 16]} className='ip-address-wizard-order-summary-coupon-billing-cycle-fields'>
                            <Col span={12}>
                                <Form.Item label='Coupon'  style={{marginBottom: '0px'}}
                                           extra={this.state.coupon !== '' && this.state.billingCycle === 'ANNUALLY' ?
                                               <span style={{color: 'red'}}><em><small>Coupon will override the default discount!</small></em></span> :
                                               <span>&nbsp;</span>}>
                                    <Input type='text' style={{display: 'block'}} value={this.state.coupon}
                                           onChange={(e) => this.setState({coupon: e.target.value})}/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label='Billing cycle' style={{marginBottom: '0px'}}>
                                    <Select style={{width: '100%'}} defaultValue={this.state.billingCycle}
                                            disabled={this.state.loadingBillingCycles}
                                            loading={this.state.loadingBillingCycles}
                                            onChange={(value) => this.setState({billingCycle: value})}>
                                        {this.state.availableBillingCycles.map((cycle, index) => {
                                            let discount = '';

                                            if (cycle.discount !== null) {
                                                if (cycle.discount.discount_type === 'PERCENTAGE') {
                                                    discount = <Tag
                                                        color='green'>{parseFloat(cycle.discount.percentage_value, 2)}%
                                                        off</Tag>
                                                }
                                            }

                                            return <Option key={index} value={cycle.name}>
                                                <Row justify='space-between'>
                                                    <Col>{normalizeEnum(cycle.name)} ({cycle.value} month{cycle.value > 1 ? 's' : ''})</Col>
                                                    {this.state.coupon === '' ? <Col>{discount}</Col> : ''}
                                                </Row>
                                            </Option>;
                                        })}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>

                <p>Your subscription will renew automatically at the end of each billing term unless canceled before the renewal date via the customer panel.</p>
                <p>By completing this order, you confirm that the Services are provided in accordance with Priority Prospect OÜ's <a href='https://priorityprospect.com/legal/terms-of-service/' rel="noopener noreferrer"  target='_blank'><b>Terms of Service</b></a> including billing, renewal, and refund provisions.</p>


            </Fragment>;

        footerButtons = <Row>
            <Col span={12} className='text-left'>
                <Button type='default' disabled={this.state.creatingOrder} onClick={() => this.closeModal()}>Cancel</Button>
            </Col>
            <Col span={12}>
                <Button type='default' disabled={this.state.creatingOrder} onClick={() => this.setState({ page: 2 })}>Previous</Button>
                <AccessItem scopes={['can_add_ip_addresses']}>
                    <Button type='primary' className='ip-address-wizard-place-order-btn' disabled={this.state.creatingOrder} loading={this.state.creatingOrder} onClick={() => this.placeOrder()}>Order Now</Button>
                </AccessItem>
            </Col>
        </Row>;
        }

        return(
            <Modal
                centered
                title={this.state.page === 0 ? 'Quick Order Wizard' : 'IP Address Wizard'}
                open={this.props.show}
                footer={footerButtons}
                destroyOnClose={true}
                width={800}
                onCancel={() => this.closeModal()}>
                {content}
            </Modal>
        );
    }
}

const mapStateToProps = state => ({
    user: state.auth.user,
    wizard: state.getting_started_wizard
});


export default connect(mapStateToProps, { loadAvailableIPAddresses, loadIPAddressGroups,
    createIPAddressOrder, loadIPAddressBillingCycles, updateDriverMultipleHighlights, driverNextStep, createQuickIPAddressOrder })(NewIPAddressesWizard);